import { Link, useRouterState } from '@tanstack/react-router'
import { Building2, BookOpen, ChevronRight } from 'lucide-react'
import { useState } from 'react'

import { NAV_GROUPS, ROUTES, type NavItem } from '@/lib/constants'
import { Settings } from 'lucide-react'
import { CommandPaletteSidebarTrigger } from '@/components/shared/command-palette'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarGroupLabel,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  useSidebar,
} from '@/components/ui/sidebar'
import { api } from '@/lib/api'
import { useQuery } from '@tanstack/react-query'
import { cn } from '@/lib/utils'

export function AppSidebar() {
  const router = useRouterState()
  const currentPath = router.location.pathname
  const { isMobile, setOpenMobile } = useSidebar()

  const handleMobileClick = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  // Check if super admin
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.settings.get(),
  })

  const isSuperAdmin = settings?.slug === 'rafobase-admin'

  return (
    <Sidebar variant="inset">
      <SidebarContent>
        <SidebarGroup className="md:hidden px-3 pt-2">
          <CommandPaletteSidebarTrigger onBeforeOpen={handleMobileClick} />
        </SidebarGroup>
        {NAV_GROUPS.map((group) => (
          <SidebarGroup key={group.label}>
            <SidebarGroupLabel className="text-[10px] font-semibold uppercase tracking-[0.08em] text-sidebar-foreground/50">
              {group.label}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => (
                  <NavMenuItem key={item.title} item={item} currentPath={currentPath} />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}

        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={currentPath.startsWith(ROUTES.SETTINGS)}
                  tooltip="Configurações"
                  onClick={handleMobileClick}
                >
                  <Link to={ROUTES.SETTINGS}>
                    <Settings />
                    <span>Configurações</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {isSuperAdmin && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-[10px] font-semibold uppercase tracking-[0.08em] text-sidebar-foreground/50">
              Backoffice
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton
                    asChild
                    isActive={currentPath === ROUTES.BACKOFFICE_MERCHANTS}
                    onClick={handleMobileClick}
                  >
                    <Link to={ROUTES.BACKOFFICE_MERCHANTS}>
                      <Building2 />
                      <span>Lojistas</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter>
        <SidebarGroup>
          <SidebarGroupLabel className="text-[10px] font-semibold uppercase tracking-[0.08em] text-sidebar-foreground/50">
            Ajuda
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <a
                    href={import.meta.env.PUBLIC_DOCS_BASE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <BookOpen />
                    <span>Documentação</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarFooter>
    </Sidebar>
  )
}

function NavMenuItem({ item, currentPath }: { item: NavItem; currentPath: string }) {
  const { isMobile, setOpenMobile } = useSidebar()
  const isExactActive = item.href === '/' ? currentPath === '/' : currentPath === item.href
  const isChildActive = item.items?.some((sub) => currentPath.startsWith(sub.href)) ?? false
  // For items with sub-items, use prefix match; for flat items, use exact match
  const hasSubItems = item.items && item.items.length > 0
  const isActive = hasSubItems
    ? isExactActive || (currentPath.startsWith(item.href) && !isChildActive)
    : isExactActive
  const shouldBeOpen = item.items?.some((sub) => currentPath.startsWith(sub.href)) ?? false

  const [isOpen, setIsOpen] = useState(shouldBeOpen)
  const [prevPath, setPrevPath] = useState(currentPath)

  if (prevPath !== currentPath) {
    setPrevPath(currentPath)
    if (shouldBeOpen) {
      setIsOpen(true)
    }
  }

  const handleMobileClick = () => {
    if (isMobile) {
      setOpenMobile(false)
    }
  }

  if (item.items && item.items.length > 0) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton
          onClick={() => setIsOpen(!isOpen)}
          isActive={isActive}
          tooltip={item.title}
          className="w-full justify-between"
        >
          <div className="flex items-center gap-2 [&>svg]:size-4 [&>svg]:shrink-0">
            <item.icon />
            <span>{item.title}</span>
          </div>
          <ChevronRight
            className={cn(
              'ml-auto size-4 transition-transform duration-200',
              isOpen && 'rotate-90'
            )}
          />
        </SidebarMenuButton>
        {isOpen && (
          <SidebarMenuSub>
            {item.items.map((subItem) => (
              <SidebarMenuSubItem key={subItem.title}>
                <SidebarMenuSubButton
                  asChild
                  isActive={currentPath === subItem.href}
                  onClick={handleMobileClick}
                >
                  <Link to={subItem.href}>
                    <span>{subItem.title}</span>
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            ))}
          </SidebarMenuSub>
        )}
      </SidebarMenuItem>
    )
  }

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        asChild
        isActive={isActive}
        tooltip={item.title}
        onClick={handleMobileClick}
      >
        <Link to={item.href}>
          <item.icon />
          <span>{item.title}</span>
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  )
}
